import { Loader2, User } from "lucide-react";
import { Avatar, AvatarFallback } from "./avatar";
import { Card, CardHeader } from "./card";
import { Input } from "./input";
import { ScrollArea } from "./scroll-area";

export interface UserListProps<T> {
  /** All users to display */
  users: T[];
  /** Loading state */
  isLoading: boolean;
  /** Search term for filtering */
  searchTerm: string;
  /** Callback when search term changes */
  onSearchChange: (value: string) => void;
  /** Callback when a user is selected */
  onUserSelect: (user: T) => void;
  /** Get the unique identifier for a user */
  getUserId: (user: T) => string;
  /** Get the display name for a user */
  getDisplayName: (user: T) => string;
  /** Check if a user is currently selected */
  isSelected: (user: T) => boolean;
  /** Optional subtitle for each user (e.g., user type, role) */
  getSubtitle?: (user: T) => string | null;
  /** Optional filter function - if not provided, filters by display name */
  filterUser?: (user: T, searchTerm: string) => boolean;
  /** Optional className for the card container */
  className?: string;
  /** Optional content rendered below the list, e.g. a "load more" button */
  footer?: React.ReactNode;
  searchControls?: React.ReactNode;
}

export function UserList<T>({
  users,
  isLoading,
  searchTerm,
  onSearchChange,
  onUserSelect,
  getUserId,
  getDisplayName,
  isSelected,
  getSubtitle,
  filterUser,
  className = "flex min-h-0 w-72 flex-col overflow-hidden border border-border/60 bg-white/80 shadow-sm max-lg:w-full",
  footer,
  searchControls,
}: UserListProps<T>) {
  const filteredUsers = users.filter((user) => {
    if (!searchTerm) return true;
    if (filterUser) return filterUser(user, searchTerm);
    const searchLower = searchTerm.toLowerCase();
    const displayName = getDisplayName(user).toLowerCase();
    const userId = getUserId(user).toLowerCase();
    return displayName.includes(searchLower) || userId.includes(searchLower);
  });

  return (
    <Card className={className}>
      {/* Header */}
      <CardHeader className="border-b">
        {/* Search */}
        <Input
          type="text"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className="h-8 text-sm"
        />
        {searchControls}
      </CardHeader>

      {/* User List */}
      <ScrollArea className="flex-1 min-h-0">
        <div className="p-2">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <p className="mt-3 text-sm text-muted-foreground">Loading...</p>
            </div>
          ) : filteredUsers.length > 0 ? (
            <div className="space-y-0.5">
              {filteredUsers.map((user) => {
                const displayName = getDisplayName(user);
                const subtitle = getSubtitle?.(user);
                return (
                  <button
                    key={getUserId(user)}
                    className={`flex w-full items-center gap-2 rounded-lg py-2 px-3 text-left transition-colors ${
                      isSelected(user) ? "bg-accent" : "hover:bg-muted"
                    }`}
                    onClick={() => onUserSelect(user)}
                  >
                    <Avatar className="size-7">
                      <AvatarFallback className="bg-muted text-xs font-medium">
                        {displayName.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-1 flex-col overflow-hidden">
                      <span className="truncate text-xs font-medium">
                        {displayName}
                      </span>
                      {subtitle && (
                        <span className="text-xs capitalize text-muted-foreground">
                          {subtitle}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <User className="h-8 w-8 text-muted-foreground/50" />
              <p className="mt-3 text-sm text-muted-foreground">
                No users found
              </p>
            </div>
          )}
          {!isLoading && footer && <div className="pt-2">{footer}</div>}
        </div>
      </ScrollArea>
    </Card>
  );
}
